import type { Plugin } from "unified"
import { visit } from "unist-util-visit"
import type { Root } from "mdast"

/**
 * AI 聊天块配置
 * 从 Markdown 标签解析而来
 */
export interface AiChatConfig {
  system?: string       // [system] 自定义指令
  ai?: string           // [ai] 欢迎消息
  goal?: string         // [goal] 本节陪练目标
  criteria?: string     // [criteria] 目标达成判断标准
}

/**
 * 解析标签行
 * 支持 [system]、[ai]、[goal]、[criteria]
 */
function parseTagLine(line: string): { tag: string; content: string } | null {
  const trimmed = line.trim()
  const tagMatches = [
    { regex: /^\[system\]\s*/i, tag: "system" },
    { regex: /^\[ai\]\s*/i, tag: "ai" },
    { regex: /^\[goal\]\s*/i, tag: "goal" },
    { regex: /^\[criteria\]\s*/i, tag: "criteria" },
  ]

  for (const { regex, tag } of tagMatches) {
    if (regex.test(trimmed)) {
      const content = trimmed.replace(regex, "").trim()
      return { tag, content }
    }
  }

  return null
}

/**
 * 解析段落中的标签
 * 支持多行内容：[tag] 后续非标签行会追加到该标签的内容中
 */
function parseParagraphTags(text: string): Partial<AiChatConfig> {
  const config: Partial<AiChatConfig> = {}

  const lines = text.split("\n")
  let currentTag: keyof AiChatConfig | null = null

  for (const line of lines) {
    const result = parseTagLine(line)
    if (result) {
      currentTag = result.tag as keyof AiChatConfig
      config[currentTag] = result.content
    } else if (currentTag) {
      const trimmed = line.trim()
      if (trimmed) {
        config[currentTag] = config[currentTag]
          ? `${config[currentTag]}\n${trimmed}`
          : trimmed
      }
    }
  }

  return config
}

/**
 * remark 插件：解析 :::ai-chat ... ::: 语法
 *
 * 支持的标签：
 * - [system] 自定义指令
 * - [ai] 欢迎消息
 * - [goal] 本节陪练目标
 * - [criteria] 目标达成判断标准
 *
 * 示例：
 * :::ai-chat
 * [goal] 让学生能够解释 React 的 useState 原理
 * [criteria] 学生能用自己的话解释 useState 的工作机制，并能举例说明
 * [system] 你是 React 专家，擅长用生活化的比喻讲解技术概念
 * [ai] 你好！我们来学习 React 的 useState，有什么问题随时问我~
 * :::
 */
export const remarkAiChat: Plugin<[], Root> = () => {
  return (tree: any) => {
    visit(tree, "containerDirective", (node: any) => {
      if (node.name !== "ai-chat") return

      const data = node.data || (node.data = {})
      const config: AiChatConfig = {}

      if (node.children) {
        for (const child of node.children) {
          if (child.type === "paragraph" && child.children) {
            // 获取段落文本
            const text = child.children
              .filter((c: any) => c.type === "text")
              .map((c: any) => c.value)
              .join("")

            // 解析段落中的标签
            const parsed = parseParagraphTags(text)
            Object.assign(config, parsed)
          }
        }
      }

      data.hName = "ai-chat"
      data.hProperties = config
    })
  }
}

